"use client";

import { useState, useEffect } from "react";
import { CategorySelector } from "./CategorySelector";
import { HouseForm } from "./forms/HouseForm";
import { AutoForm } from "./forms/AutoForm";
import { ElectronicsForm } from "./forms/ElectronicsForm";
import { ApplianceForm } from "./forms/ApplianceForm";
import { SubleaseForm } from "./forms/SubleaseForm";
import { ApparelForm } from "./forms/ApparelForm";
import { postClassfieds } from "../../services/classified";
import { successToast,errorToast } from '../../utils/toasters'
import { RoommateForm } from "./forms/RoommateForm";
import { isAuthenticated } from "../../utils/helpers";
import { useNavigate } from "react-router-dom";

export function ClassifiedContent({ onClose, onItemCreated }) {
  const [category, setCategory] = useState(null);
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const navigate = useNavigate();

  const maxSteps = 7;

  // Auto-submit pending classified data after login
  useEffect(() => {
    const pendingData = localStorage.getItem("pendingClassifiedData");
    if (pendingData && isAuthenticated()) {
      try {
        const parsedData = JSON.parse(pendingData);

        // Show toaster message when completing saved posting
        successToast("Completing your saved classified posting...", {
          position: "top-right",
          autoClose: 3000,
        });

        submitClassified(parsedData);
        localStorage.removeItem("pendingClassifiedData");
      } catch (error) {
        console.error("Error parsing pending classified data:", error);
        localStorage.removeItem("pendingClassifiedData");
        errorToast("Failed to restore your saved posting. Please try again.");
      }
    }
  }, []);

  const handleCategorySelect = (selectedId) => {
    setCategory(selectedId);
    setStep(1);
    setFormData({
      categoryId: selectedId,
    });
  };

  const saveClassifiedToStorage = (data, categoryId) => {
    const classifiedData = {
      ...data,
      category: categoryId,
      timestamp: Date.now(),
    };
    localStorage.setItem(
      "pendingClassifiedData",
      JSON.stringify(classifiedData)
    );
  };

  // Convert data URL to File
  const dataUrlToFile = (dataUrl, fileName) => {
    return new Promise((resolve, reject) => {
      try {
        if (!dataUrl || !dataUrl.includes(",")) {
          reject(new Error("Invalid data URL format"));
          return;
        }

        const arr = dataUrl.split(",");
        const mimeMatch = arr[0].match(/:(.*?);/);
        if (!mimeMatch) {
          reject(new Error("Could not extract MIME type"));
          return;
        }

        const mime = mimeMatch[1];
        const bstr = atob(arr[1]);
        const u8arr = new Uint8Array(bstr.length);

        for (let i = 0; i < bstr.length; i++) {
          u8arr[i] = bstr.charCodeAt(i);
        }

        resolve(new File([u8arr], fileName, { type: mime }));
      } catch (error) {
        reject(error);
      }
    });
  };

  // Convert File to data URL for storage
  const fileToDataUrl = (file) => {
    return new Promise((resolve, reject) => {
      if (!(file instanceof File)) {
        reject(new Error("Invalid file object"));
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = () => reject(new Error("FileReader error"));
      reader.readAsDataURL(file);
    });
  };

  const submitClassified = async (dataToSubmit = null) => {
    if (isProcessing) {
      return;
    }

    const submissionData = dataToSubmit || formData;
    const categoryToUse = dataToSubmit?.category || category;

    try {
      setIsProcessing(true);
      const geohash = localStorage.getItem("geohash") || "9v6m";
      const submissionFormData = new FormData();

      // Handle cover photo
      if (submissionData.images?.[0]) {
        let coverPhotoFile = null;

        if (submissionData.images[0].file instanceof File) {
          coverPhotoFile = submissionData.images[0].file;
        } else if (submissionData.images[0].dataUrl) {
          coverPhotoFile = await dataUrlToFile(
            submissionData.images[0].dataUrl,
            submissionData.images[0].name || "cover_photo.jpg"
          );
        }

        if (coverPhotoFile instanceof File) {
          submissionFormData.append("cover_photo", coverPhotoFile);
        } else {
          errorToast(
            "Invalid cover photo format. Please upload a valid image file."
          );
          setIsProcessing(false);
          return;
        }
      } else {
        errorToast("Please add at least one image for your listing.");
        setIsProcessing(false);
        return;
      }

      // Handle additional images
      if (submissionData.images?.length > 1) {
        for (let i = 1; i < submissionData.images.length; i++) {
          let additionalImageFile = null;

          if (submissionData.images[i].file instanceof File) {
            additionalImageFile = submissionData.images[i].file;
          } else if (submissionData.images[i].dataUrl) {
            try {
              additionalImageFile = await dataUrlToFile(
                submissionData.images[i].dataUrl,
                submissionData.images[i].name || `image_${i}.jpg`
              );
            } catch (error) {
              continue; // Skip corrupted images
            }
          }

          if (additionalImageFile instanceof File) {
            submissionFormData.append("product_images", additionalImageFile);
          }
        }
      }

      // Validate required fields
      const requiredFields = {
        title: submissionData.title,
        price: submissionData.price,
        fullName: submissionData.fullName,
        phoneNumber: submissionData.phoneNumber,
        email: submissionData.email,
        category_id: categoryToUse,
      };

      const missingFields = Object.entries(requiredFields)
        .filter(([key, value]) => !value || value.toString().trim() === "")
        .map(([key]) => key);

      if (missingFields.length > 0) {
        errorToast(`Missing required fields: ${missingFields.join(", ")}`);
        setIsProcessing(false);
        return;
      }

      // Append form data
      submissionFormData.append("title", submissionData.title);
      submissionFormData.append("price", submissionData.price);
      submissionFormData.append(
        "reason_for_selling",
        submissionData.story || ""
      );
      submissionFormData.append(
        "more_description",
        submissionData.description || ""
      );
      submissionFormData.append("full_name", submissionData.fullName);
      submissionFormData.append("category_id", categoryToUse);
      submissionFormData.append("phone_number", submissionData.phoneNumber);
      submissionFormData.append("address1", submissionData.addressLine1 || "");
      submissionFormData.append("address2", submissionData.addressLine2 || "");
      submissionFormData.append("email", submissionData.email);
      submissionFormData.append("state", submissionData.state || "");
      submissionFormData.append("geohash", geohash);

      // Handle specific details
      if (
        submissionData.specific_details &&
        Object.keys(submissionData.specific_details).length > 0
      ) {
        Object.entries(submissionData.specific_details).forEach(
          ([key, value]) => {
            submissionFormData.append("specific_details", `${key}:${value}`);
          }
        );
      }

      // Submit to API
      const response = await postClassfieds(submissionFormData);

      if (onItemCreated) {
        onItemCreated(response.data);
      }

      handleClose();

      requestAnimationFrame(() => {
        successToast("Your classified has been posted successfully!", {
          position: "top-right",
          autoClose: 5000,
        });
      });
    } catch (error) {
      console.error("Error posting classified:", error);

      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.response?.data?.details ||
        "Failed to post your classified. Please try again.";

      errorToast(errorMessage, {
        position: "top-right",
        autoClose: 7000,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleNext = async () => {
    if (isProcessing) {
      return;
    }

    if (step < maxSteps) {
      setStep(step + 1);
    } else {
      if (!isAuthenticated()) {
        try {
          const dataForStorage = { ...formData };
          // Convert files to base64 for storage
          if (formData.images?.length > 0) {
            successToast("Saving your progress...", {
              position: "top-right",
              autoClose: 2000,
            });

            dataForStorage.images = await Promise.all(
              formData.images.map(async (img, index) => {
                if (img.file instanceof File) {
                  const dataUrl = await fileToDataUrl(img.file);
                  return {
                    ...img,
                    dataUrl,
                    name: img.file.name,
                    size: img.file.size,
                    type: img.file.type,
                  };
                }
                return img;
              })
            );
          }

          saveClassifiedToStorage(dataForStorage, category);

          successToast(
            "Your progress has been saved! Please sign in to complete your posting.",
            {
              position: "top-right",
              autoClose: 5000,
            }
          );

          handleClose();

          const currentUrl = window.location.pathname + window.location.search;
          navigate(`/signin?returnUrl=${encodeURIComponent(currentUrl)}`);
          return;
        } catch (error) {
          console.error("Error saving data:", error);
          errorToast("Failed to save your progress. Please try again.");
          return;
        }
      }

      await submitClassified();
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    } else {
      setCategory(null);
    }
  };

  const handleClose = () => {
    setCategory(null);
    setStep(1);
    setFormData({});
    onClose();
  };

  const updateFormData = (data) => {
    setFormData((prev) => ({
      ...prev,
      ...data,
    }));
  };

  const renderForm = () => {
    if (!category) {
      return <CategorySelector onSelect={handleCategorySelect} />;
    }

    const formProps = {
      step,
      maxSteps,
      onNext: handleNext,
      onBack: handleBack,
      onClose: handleClose,
      formData,
      updateFormData,
      isProcessing,
    };

    switch (category) {
      case 24:
        return <HouseForm {...formProps} />;
      case 25:
        return <AutoForm {...formProps} />;
      case 26:
        return <SubleaseForm {...formProps} />;
      case 27:
        return <ElectronicsForm {...formProps} />;
      case 28:
        return <ApplianceForm {...formProps} />;
      case 29:
        return <ApparelForm {...formProps} />;
      case 30:
        return <RoommateForm {...formProps} />;
      default:
        return <CategorySelector onSelect={handleCategorySelect} />;
    }
  };

  return <div className="p-0 md:p-0">{renderForm()}</div>;
}
